import { Component, h, Prop, State, Element, Build } from '@stencil/core';
import { parseJsonProp } from '../../../utils/helpers/hydration';

const COMPONENT_NAME = 'testimonials-type-4';

/**
 * Testimonial carousel component with navigation controls (arrows and dots).
 * Displays testimonials in a slider format with content on the left and decoration image on the right.
 *
 * @category Testimonials
 * @source reviews
 * @slot heading - Section heading. Rendered when no `title` prop is provided. Use <me-heading>
 * @slot subtitle - Section subtitle. Rendered when no `subtitle` prop is provided. Use <me-text>
 * @slot decoration - {required}, <me-picture>
 * @example
 *   <testimonials-type-4
 *     title="What our customers say"
 *     subtitle="Read testimonials from our satisfied customers"
 *     items='[{"header": "Excellent resource", "stars": 5, "author": "John Doe", "reviewRawHtml": "<p>Great product!</p>", "reviewPlatform": {"href": "https://example.com/review", "hrefTitle": "Read full review"}}]'
 *   >
 *     <me-picture slot="decoration" src="https://example.com/image.jpg" alt="Decoration"></me-picture>
 *   </testimonials-type-4>
 */
@Component({
  tag: 'testimonials-type-4',
  styleUrl: 'index.scss',
  shadow: false,
})
export class TestimonialsType4 {
  @Element() el: HTMLElement;

  /**
   * Array of review items to be displayed in the carousel.
   * @content
   * @dynamic
   * @type {array}
   * @items.type {object}
   * @items.property {string} header - Review heading/title
   * @items.property {number} stars - Review stars rating (1-5)
   * @items.property {string} author - Review author name
   * @items.property {string} reviewRawHtml - Review content as raw HTML
   * @items.property {object} reviewPlatform - Platform link information
   * @items.property {string} reviewPlatform.href - URL for the "read more" link
   * @items.property {string} reviewPlatform.hrefTitle - Title for the "read more" link
   * @items.required header
   * @items.required stars
   * @items.required author
   * @items.required reviewRawHtml
   * @items.required reviewPlatform
   * @items.content header author
   * @minItems 1
   */
  @Prop({ mutable: true }) items!: Array<{
    header: string;
    stars: number;
    author: string;
    reviewRawHtml?: string; // Legacy support, raw_html is preferred
    raw_html?: string; // Optimized for SSR: bypasses innerHTML parsing, prefer over reviewRawHtml
    reviewPlatform: {
      href: string;
      hrefTitle: string;
    };
  }>;

  /**
   * Section title
   * @content
   * @dynamic
   * @type {string}
   */
  @Prop({ attribute: 'title' }) heading?: string;

  /**
   * HTML tag for the section heading element (h1, h2, h3, h4, h5, h6)
   * @default "h2"
   */
  @Prop() headingTag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' = 'h2';

  /**
   * Section subtitle
   * @content
   * @dynamic
   * @type {string}
   */
  @Prop() subtitle?: string;

  /**
   * Data payload containing title, subTitle and items
   * @dynamic
   */
  @Prop({ mutable: true }) values?:
    | {
        title?: string;
        subTitle?: string;
        items?: Array<{
          header: string;
          stars?: number;
          rating?: number;
          author: string;
          reviewRawHtml?: string;
          raw_html?: string;
          reviewPlatform: {
            href: string;
            hrefTitle: string;
          };
        }>;
      }
    | string;

  /**
   * Enable/disable carousel autoplay
   * @default false
   */
  @Prop() autoplay: boolean = false;

  /**
   * Autoplay interval in milliseconds
   * @default 5000
   */
  @Prop() autoplayInterval: number = 5000;

  /**
   * ARIA label for carousel navigation container
   * @default "Carousel navigation"
   */
  @Prop() ariaLabelCarouselNavigation: string = 'Carousel navigation';

  /**
   * ARIA label template for slide navigation dots. Use {index} placeholder for slide number (1-based).
   * @default "Go to slide {index}"
   */
  @Prop() ariaLabelSlide: string = 'Go to slide {index}';

  /**
   * ARIA label for previous slide button
   * @default "Previous slide"
   */
  @Prop() ariaLabelPrevious: string = 'Previous slide';

  /**
   * ARIA label for next slide button
   * @default "Next slide"
   */
  @Prop() ariaLabelNext: string = 'Next slide';

  /**
   * ARIA label template for rating. Use {rating} placeholder for rating value and {max} for maximum rating.
   * @default "Rated {rating} out of {max} stars"
   */
  @Prop() ariaLabelRating: string = 'Rated {rating} out of {max} stars';

  /**
   * Maximum rating value for Schema.org and ARIA labels
   * @default "5"
   */
  @Prop() maxRating: string = '5';

  @State() currentSlide: number = 0;
  @State() isTransitioning: boolean = false;

  private autoplayTimer?: number;
  private touchStartX: number = 0;
  private touchEndX: number = 0;

  private isParsed = false;

  parseItems() {
    // Prevent infinite loop: if items is already an array and parsed, skip
    if (this.isParsed && Array.isArray(this.items) && this.items.length > 0 && this.items[0]?.stars !== undefined) {
      return;
    }

    let rawItems: any[] = [];

    // Handle items that come as string (from HTML attributes during hydration)
    if (Array.isArray(this.items)) {
      rawItems = this.items;
    } else if (typeof this.items === 'string') {
      try {
        rawItems = JSON.parse((this.items as string).replace(/&quot;/g, '"'));
      } catch (e) {
        console.error('Failed to parse items:', e);
        rawItems = [];
      }
    } else {
      // Fallback: try to get from attribute
      const itemsAttr = this.el.getAttribute('items');
      if (itemsAttr) {
        try {
          rawItems = JSON.parse(itemsAttr.replace(/&quot;/g, '"'));
        } catch (e) {
          console.error('Failed to parse items from attribute:', e);
          rawItems = [];
        }
      }
    }

    // Normalize data: convert rating to stars if needed
    const normalizedItems = Array.isArray(rawItems)
      ? rawItems.map(item => ({
          ...item,
          stars: item.stars !== undefined && item.stars > 0 ? item.stars : item.rating && item.rating > 0 ? Number(item.rating) : 5,
        }))
      : [];

    // Only update if different to prevent infinite loop
    if (!this.isParsed || !Array.isArray(this.items) || this.items.length !== normalizedItems.length) {
      this.isParsed = true;
      this.items = normalizedItems;
    }
  }

  componentWillLoad() {
    this.values = parseJsonProp(this.values);

    if (this.values && typeof this.values !== 'string' && this.values.items && (!this.items || !this.items.length)) {
      this.items = this.values.items as any;
    }

    this.parseItems();
    if (!this.items || this.items.length === 0) {
      return;
    }
  }

  componentDidLoad() {
    // Re-parse in case items were set after componentWillLoad
    this.parseItems();
    // Only run client-side code (skip during SSR)
    if (Build.isBrowser) {
      if (this.autoplay && this.items && Array.isArray(this.items) && this.items.length > 1) {
        this.startAutoplay();
      }
      this.updateTabIndexForHiddenSlides();
    }
  }

  componentDidUpdate() {
    // Only run client-side code (skip during SSR)
    if (Build.isBrowser) {
      this.updateTabIndexForHiddenSlides();
    }
  }

  private updateTabIndexForHiddenSlides() {
    // Skip during SSR - DOM manipulation is not needed server-side
    if (!Build.isBrowser) {
      return;
    }

    // Find all slides
    const slides = this.el.querySelectorAll(`.${COMPONENT_NAME}__slide`);

    slides.forEach((slide, index) => {
      const isActive = index === this.currentSlide;
      const focusableElements = slide.querySelectorAll<HTMLElement>('a, button, [tabindex]:not([tabindex="-1"]), input, select, textarea');

      focusableElements.forEach(element => {
        if (!isActive) {
          // Set tabindex to -1 for hidden slides to prevent keyboard focus
          // Only set if not already -1 to avoid unnecessary DOM updates
          if (element.getAttribute('tabindex') !== '-1') {
            element.setAttribute('tabindex', '-1');
          }
        } else {
          // Restore focusability for active slide
          // Remove tabindex="-1" to restore default focusable behavior
          if (element.getAttribute('tabindex') === '-1') {
            element.removeAttribute('tabindex');
          }
        }
      });
    });
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  private startAutoplay() {
    if (!Build.isBrowser) return;
    this.stopAutoplay();
    this.autoplayTimer = window.setInterval(() => {
      this.nextSlide();
    }, this.autoplayInterval);
  }

  private stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = undefined;
    }
  }

  private goToSlide(index: number) {
    if (this.isTransitioning || index === this.currentSlide) return;
    if (index < 0 || index >= this.items.length) return;

    this.isTransitioning = true;
    this.currentSlide = index;

    // Wait for the next frame to ensure DOM is updated with transitioning class
    requestAnimationFrame(() => {
      // Then wait for transition duration before removing transitioning class
      // This ensures smooth fade from 0.7 to 1.0 opacity
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.isTransitioning = false;
        }, 300);
      });
    });

    if (this.autoplay) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  private nextSlide() {
    const nextIndex = (this.currentSlide + 1) % this.items.length;
    this.goToSlide(nextIndex);
  }

  private prevSlide() {
    const prevIndex = (this.currentSlide - 1 + this.items.length) % this.items.length;
    this.goToSlide(prevIndex);
  }

  private handleTouchStart = (e: TouchEvent) => {
    this.touchStartX = e.touches[0].clientX;
    this.stopAutoplay();
  };

  private handleTouchMove = (e: TouchEvent) => {
    this.touchEndX = e.touches[0].clientX;
  };

  private handleTouchEnd = () => {
    if (!this.touchStartX || !this.touchEndX) return;

    const diff = this.touchStartX - this.touchEndX;
    const minSwipeDistance = 50;

    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0) {
        // Swipe left - next slide
        this.nextSlide();
      } else {
        // Swipe right - previous slide
        this.prevSlide();
      }
    }

    this.touchStartX = 0;
    this.touchEndX = 0;

    if (this.autoplay) {
      this.startAutoplay();
    }
  };

  private renderRatingStars(stars: number) {
    const ratingValue = stars;

    return [0, 1, 2, 3, 4].map(i => {
      const fillWidth = String(Math.round(Math.max(0, Math.min(100, (ratingValue - i) * 100))));
      return h('rating-star', { 'aria-hidden': 'true', 'key': i, 'fillWidth': fillWidth });
    });
  }

  private formatTemplate(template: string, replacements: Record<string, string | number>): string {
    return template.replace(/\{(\w+)\}/g, (match, key) => {
      return replacements[key] !== undefined ? String(replacements[key]) : match;
    });
  }

  private getAriaLabelSlide(index: number): string {
    return this.formatTemplate(this.ariaLabelSlide, { index: index + 1 });
  }

  private getAriaLabelRating(stars: number): string {
    return this.formatTemplate(this.ariaLabelRating, { rating: String(stars), max: this.maxRating });
  }

  private renderReviewSlide(item: any, isActive: boolean) {
    const isTransitioning = this.isTransitioning && isActive;

    return (
      <article
        {...({
          itemscope: true,
          itemtype: 'https://schema.org/Review',
        } as any)}
        class={`${COMPONENT_NAME}__slide ${isActive ? `${COMPONENT_NAME}__slide--active` : `${COMPONENT_NAME}__slide--hidden`} ${isTransitioning ? `${COMPONENT_NAME}__slide--transitioning` : ''}`}
        aria-hidden={!isActive ? 'true' : 'false'}
      >
        <div class={`${COMPONENT_NAME}__content`}>
          {item.header && (
            <me-heading variant="3" tag="h3" class={`${COMPONENT_NAME}__heading`} itemProp="name">
              {item.header}
            </me-heading>
          )}
          <div class={`${COMPONENT_NAME}__rating-author`}>
            <div
              {...({
                itemprop: 'reviewRating',
                itemscope: true,
                itemtype: 'https://schema.org/Rating',
              } as any)}
              role="img"
              aria-label={this.getAriaLabelRating(item.stars)}
              class={`${COMPONENT_NAME}__rating`}
            >
              <meta {...({ itemprop: 'ratingValue' } as any)} content={String(item.stars && item.stars > 0 ? item.stars : 5)} />
              <meta {...({ itemprop: 'bestRating' } as any)} content={this.maxRating} />
              {this.renderRatingStars(item.stars)}
            </div>
            {item.author && (
              <div {...({ itemprop: 'author', itemscope: true, itemtype: 'https://schema.org/Person' } as any)}>
                <me-text tag="div" weight="semi-bold" class={`${COMPONENT_NAME}__author`} itemProp="name">
                  {item.author}
                </me-text>
              </div>
            )}
          </div>
          {(item.raw_html ?? item.reviewRawHtml) && (
            <div
              {...({ itemprop: 'reviewBody' } as any)}
              class={`${COMPONENT_NAME}__review-content`}
              innerHTML={item.raw_html ?? item.reviewRawHtml}
              aria-hidden={!isActive ? 'true' : undefined}
            />
          )}
          {item.reviewPlatform && item.reviewPlatform.href && item.reviewPlatform.hrefTitle && (
            <me-button
              tag="a"
              variant="ghost"
              href={item.reviewPlatform.href}
              icon="arrows--chevron-right"
              iconPosition="after"
              class={`${COMPONENT_NAME}__read-more`}
              tabindex={!isActive ? -1 : undefined}
              aria-hidden={!isActive ? 'true' : undefined}
            >
              {item.reviewPlatform.hrefTitle}
            </me-button>
          )}
        </div>
      </article>
    );
  }

  render() {
    // Ensure items is parsed and is an array
    if (!Array.isArray(this.items) || this.items.length === 0) {
      return null;
    }

    const hasMultipleSlides = this.items.length > 1;
    const parsedValues = this.values && typeof this.values !== 'string' ? this.values : undefined;
    const heading = parsedValues?.title || this.heading;
    const subtitle = parsedValues?.subTitle || this.subtitle;

    return (
      <me-section yGap="2xl" yGapLg="5xl" class={COMPONENT_NAME} role="region" aria-label="Customer Testimonials">
        <me-row yGap="4xl" yGapLg="6xl">
          <me-column span="12" class={`${COMPONENT_NAME}__intro`}>
            {heading ? (
              <me-heading variant="2" align="center" tag={this.headingTag}>
                {heading}
              </me-heading>
            ) : (
              <slot name="heading"></slot>
            )}
            {subtitle ? (
              <me-text variant="medium" align="center">
                {subtitle}
              </me-text>
            ) : (
              <slot name="subtitle"></slot>
            )}
          </me-column>
          <me-column span="12">
            <me-row align="center">
              <me-column span="12" spanLg="6">
                <div
                  class={`${COMPONENT_NAME}__carousel`}
                  onTouchStart={this.handleTouchStart}
                  onTouchMove={this.handleTouchMove}
                  onTouchEnd={this.handleTouchEnd}
                  onMouseEnter={() => this.autoplay && this.stopAutoplay()}
                  onMouseLeave={() => this.autoplay && this.startAutoplay()}
                >
                  {this.items.map((item, index) => this.renderReviewSlide(item, index === this.currentSlide))}
                </div>
              </me-column>
              <me-column span="12" spanLg="6" class={`${COMPONENT_NAME}__decoration`}>
                <slot name="decoration"></slot>
              </me-column>
            </me-row>
            {hasMultipleSlides && (
              <div class={`${COMPONENT_NAME}__navigation`}>
                <div class={`${COMPONENT_NAME}__dots`} role="tablist" aria-label={this.ariaLabelCarouselNavigation}>
                  {this.items.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      role="tab"
                      aria-selected={index === this.currentSlide ? 'true' : 'false'}
                      aria-label={this.getAriaLabelSlide(index)}
                      class={`${COMPONENT_NAME}__dot ${index === this.currentSlide ? `${COMPONENT_NAME}__dot--active` : ''}`}
                      onClick={() => this.goToSlide(index)}
                    />
                  ))}
                </div>
                <div class={`${COMPONENT_NAME}__arrows`}>
                  <me-button
                    tag="button"
                    variant="ghost"
                    size="sm"
                    ariaLabel={this.ariaLabelPrevious}
                    icon="arrows--chevron-left"
                    iconPosition="only"
                    onClick={() => this.prevSlide()}
                    class={`${COMPONENT_NAME}__arrow ${COMPONENT_NAME}__arrow--prev`}
                  />
                  <me-button
                    tag="button"
                    variant="ghost"
                    size="sm"
                    ariaLabel={this.ariaLabelNext}
                    icon="arrows--chevron-right"
                    iconPosition="only"
                    onClick={() => this.nextSlide()}
                    class={`${COMPONENT_NAME}__arrow ${COMPONENT_NAME}__arrow--next`}
                  />
                </div>
              </div>
            )}
          </me-column>
        </me-row>
      </me-section>
    );
  }
}
